import { MouseEvent, useState } from 'react';

import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import ShutterSpeedIcon from '@mui/icons-material/ShutterSpeed';
import TimerIcon from '@mui/icons-material/Timer';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import AppDateTimePicker from 'src/common/components/form/AppDateTimePicker';
import { useTranslation } from 'react-i18next';

import TicketCard from 'src/common/components/styled/TicketCard';
import TicketCardEditDeleteFooter from 'src/common/components/styled/TicketCardEditDeleteFooter';
import RegistrationAvatar from 'src/common/components/RegistrationAvatar';
import { ticketCardBadgeSx } from 'src/common/styles/ticketCardStyles';
import { LanguageKeys } from 'src/common/constants/languages';
import { minutesToHoursShort } from 'src/helpers/dateTimeFormatters';
import dayjs, { toDayjs } from 'src/helpers/dayjs';
import { ECheckinTimerType, ICheckin } from 'src/interfaces/Checkin';
import { ICompetition } from 'src/interfaces/Competition';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

interface Props {
  checkin: ICheckin;
  competition: ICompetition;
  distanceLabel: string;
  currentLang: LanguageKeys;
  onClickTimer: (id: string, type: ECheckinTimerType, timeStamp?: Date) => void;
  onSetDnf: (id: string, dnf: boolean) => void;
  onDeleteCheckin: (checkinId: string, e?: MouseEvent<unknown>) => void;
  onEditCheckin: (checkinId: string, e?: MouseEvent<unknown>) => void;
}

const CheckinCard = ({
  checkin: {
    id,
    participant,
    ser,
    startTime: st,
    finishTime: ft,
    duration,
    dnf = false,
  },
  competition,
  distanceLabel,
  currentLang,
  onClickTimer,
  onSetDnf,
  onDeleteCheckin,
  onEditCheckin,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { dateTimeFormat } = useSystemOptionsStore(systemOptionsState => systemOptionsState);

  const [isEditStartTime, setEditStartTime] = useState(false);
  const [isEditFinishTime, setEditFinishTime] = useState(false);
  const [startTime, handleStartTimeChange] = useState<Date | undefined>(st);
  const [finishTime, handleFinishTimeChange] = useState<Date | undefined>(ft);

  if (!participant) {
    return null;
  }

  const { firstName, lastName } = participant;

  const handleEditStartTime = () => {
    if (isEditStartTime && !!startTime) onClickTimer(id, ECheckinTimerType.start, startTime);
    setEditStartTime(!isEditStartTime);
  };

  const handleEditFinishTime = () => {
    if (isEditFinishTime && !!finishTime) onClickTimer(id, ECheckinTimerType.finish, finishTime);
    setEditFinishTime(!isEditFinishTime);
  };

  const startTimeValue = toDayjs(startTime);
  const finishTimeValue = toDayjs(finishTime);

  const dnfButton = startTime ? (
    <Tooltip title={dnf ? t('timers.clearDnf') : t('timers.setDnf')}>
      <IconButton
        aria-label={dnf ? t('timers.clearDnf') : t('timers.setDnf')}
        onClick={() => onSetDnf(id, !dnf)}
        size="small"
        color={dnf ? 'error' : 'default'}
      >
        <BlockIcon fontSize="large" />
      </IconButton>
    </Tooltip>
  ) : null;

  return (
    <TicketCard
      eyebrow={
        <Box
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
        >
          {t('checkins.ser')}
          <Chip label={String(ser)} size="small" sx={ticketCardBadgeSx(theme, true, false)} />
          {dnf ? (
            <Chip
              label={t('timers.dnf')}
              size="small"
              color="error"
              variant="outlined"
            />
          ) : null}
        </Box>
      }
      title={`${lastName} ${firstName}`}
      description={
        duration ? (
          <Chip
            label={minutesToHoursShort(duration, t)}
            size="small"
            sx={ticketCardBadgeSx(theme, false, false)}
          />
        ) : startTime ? (
          dayjs(startTime).format(dateTimeFormat[currentLang])
        ) : (
          t('timers.startTimer')
        )
      }
      avatar={
        <RegistrationAvatar
          competition={competition}
          distanceLabel={distanceLabel}
          currentLang={currentLang}
        />
      }
      footer={
        <TicketCardEditDeleteFooter
          popupId={`checkin-tools-${id}`}
          optionsAriaLabel={t('participants.optionsLabel')}
          editLabel={t('checkins.editCheckin')}
          deleteLabel={t('checkins.deleteCheckin')}
          onEdit={e => onEditCheckin(id, e)}
          onDelete={e => onDeleteCheckin(id, e)}
        />
      }
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        {t('timers.start')}:
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
      >
        {startTime ? (
          <>
            {!isEditStartTime ? (
              dayjs(startTime).format(dateTimeFormat[currentLang])
            ) : (
              <AppDateTimePicker
                ampm={false}
                openTo="hours"
                maxDateTime={finishTimeValue ?? undefined}
                value={startTimeValue}
                onChange={newValue => {
                  handleStartTimeChange((newValue?.toDate() as Date) || undefined);
                }}
              />
            )}
            <Tooltip title={isEditStartTime ? t('timers.save') : t('timers.editTimer')}>
              <IconButton
                aria-label={isEditStartTime ? t('timers.save') : t('timers.editTimer')}
                onClick={handleEditStartTime}
                edge="end"
                size="small"
              >
                {isEditStartTime ? (
                  <CheckIcon sx={{ fontSize: 14 }} />
                ) : (
                  <EditIcon sx={{ fontSize: 14 }} />
                )}
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Tooltip title={t('timers.startTimer')}>
            <IconButton
              aria-label={t('timers.startTimer')}
              onClick={() => onClickTimer(id, ECheckinTimerType.start)}
              size="small"
            >
              <TimerIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        )}
      </Typography>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {t('timers.finish')}:
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5 }}
      >
        {!!startTime && !finishTime ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={t('timers.stopTimer')}>
              <IconButton
                aria-label={t('timers.stopTimer')}
                onClick={() => onClickTimer(id, ECheckinTimerType.finish)}
                size="small"
              >
                <ShutterSpeedIcon fontSize="large" />
              </IconButton>
            </Tooltip>
            {dnfButton}
          </Box>
        ) : finishTime ? (
          <>
            {!isEditFinishTime ? (
              dayjs(finishTime).format(dateTimeFormat[currentLang])
            ) : (
              <AppDateTimePicker
                ampm={false}
                openTo="hours"
                minDateTime={startTimeValue ?? undefined}
                value={finishTimeValue}
                onChange={newValue => {
                  handleFinishTimeChange((newValue?.toDate() as Date) || undefined);
                }}
              />
            )}
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              {dnfButton}
              <Tooltip title={isEditFinishTime ? t('timers.save') : t('timers.editTimer')}>
                <IconButton
                  aria-label={isEditFinishTime ? t('timers.save') : t('timers.editTimer')}
                  onClick={handleEditFinishTime}
                  edge="end"
                  size="small"
                >
                  {isEditFinishTime ? (
                    <CheckIcon sx={{ fontSize: 14 }} />
                  ) : (
                    <EditIcon sx={{ fontSize: 14 }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </>
        ) : (
          '-'
        )}
      </Typography>
    </TicketCard>
  );
};

export default CheckinCard;
